<template>
  <div
    :class="{ 'dea-product-card--app': isApp(type), 'dea-product-card--book': isBook(type) }"
    class="dea-product-card"
    data-test="product__card-book--container"
  >
    <div class="dea-product-card__image-container">
      <div
        v-if="showBookmark"
        class="dea-product-card__important-icon"
        :class="{ 'dea-product-card__important-icon--select': bookmark }"
        :data-test="`bookmark-save-button-${timeId}`"
        @click="toggleBookmark"
      >
        <img
          :src="bookmark ? importantIcoNeg : importantIcoPos"
          alt="icona importanti"
          aria-label="icona importanti"
          @mouseenter="toggleTooltip"
          @mouseleave="toggleTooltip"
        />
      </div>
      <Tooltip v-if="showTooltip" class="dea-tooltip--book" side="right" position="top" :negative="false">
        <p v-if="bookmark" class="dea-font-white">segnato come importante</p>
        <p v-else class="dea-font-white">segna come importante</p>
      </Tooltip>
      <component
        :is="linkType"
        :target="target"
        :href="link"
        class="dea-product-card__image-wrapper"
        v-on="$listeners"
        @click.prevent="handleClick($event, 'cover-click')"
      >
        <div :style="{ backgroundImage: `url('${image}')` }" class="dea-product-card__image" :aria-label="title">
          <Badge v-if="badge === 'suspended'" class="dea-product-card__badge dea-badge__new dea-bg-red-negative"
            >Sospeso</Badge
          >
          <Badge v-if="badge === 'app'" class="dea-product-card__badge dea-badge__app dea-bg-grey-dark">App</Badge>
          <Badge v-if="badge === 'new'" class="dea-product-card__badge dea-badge__new dea-bg-red-negative"
            >Novità</Badge
          >
          <Badge v-if="badge === 'newActivation'" class="dea-product-card__badge dea-badge__new dea-bg-red-negative"
            >Nuovo</Badge
          >
          <Badge v-if="badge === 'demo'" class="dea-product-card__badge dea-badge__new dea-bg-red-negative">Demo</Badge>
          <div
            v-if="expired || (fuoriCatalogo && activable) || suspended"
            class="dea-product-card__disabled-overlay"
          ></div>
          <Buttons
            v-if="fuoriCatalogo && activable"
            class="dea-button--small dea-button--white dea-button--iconLeft dea-product-card__button--activate"
            icon="attiva"
            >Attiva libro</Buttons
          >
        </div>
      </component>
      <div v-if="volumes !== null" class="dea-product-card__volumes dea-text--center dea-heading--medium__xm">
        <template v-if="volumes">{{ volumes }} volum{{ volumes !== 1 ? "i" : "e" }}</template>
        <template v-else>{{ `\xa0` }}</template>
        <!--\xa0 is the non breaking space to keep vertical alignment consistent -->
      </div>
    </div>
    <div v-if="cardType === 'compact'" class="dea-product-card__description">
      <p class="dea-product-card__title">{{ title }}</p>
      <p v-if="expired" class="dea-product-card__expired-message">
        <span class="dea-product-card__expired-icon dea-ico-error"></span>
        <span class="dea-product-card__expired-message-text">Periodo di valutazione scaduto</span>
      </p>
      <p v-if="!expired && expirationDate !== ''" class="dea-product-card__activation-date">
        In Valutazione fino al {{ expirationDate }}
      </p>
      <Links
        v-if="showCatalogLink"
        class="dea-product-card__book-link dea-links--small"
        :class="`dea-links--${linkColor}`"
        :href="catalogLink"
      >
        Scheda libro nel Catalogo
      </Links>
    </div>
    <div v-if="cardType === 'normal'" class="dea-product-card__description">
      <p class="dea-product-card__title" @click.prevent="handleClick($event, 'title-click')">
        {{ title }}
      </p>
      <p v-if="fuoriCatalogo && activable" class="dea-head-product__activate-cta__text-top-warning">
        Puoi attivare l'opera anche se è fuori catalogo
      </p>
      <template v-if="showExtraInfo">
        <template v-if="!activationDetails">
          <p v-if="subtitle !== ''" class="dea-product-card__subtitle">{{ subtitle }}</p>
          <p v-if="schoolTarget !== ''" class="dea-product-card__subtitle dea-heading--regular__xm">
            {{ schoolTarget }}
          </p>
          <p v-if="collection !== ''" class="dea-product-card__subtitle dea-heading--regular__xm">{{ collection }}</p>
          <p v-if="level !== ''" class="dea-product-card__subtitle dea-heading--medium__xm">Livello {{ level }}</p>
          <p v-if="author" class="dea-product-card__author">{{ author }}</p>
          <p v-if="editor" class="dea-product-card__editor">{{ editor }}</p>
        </template>

        <template v-else>
          <p v-if="expired" class="dea-product-card__expired-message">
            <span class="dea-product-card__expired-icon dea-ico-error"></span>
            <span class="dea-product-card__expired-message-text">Periodo di valutazione scaduto</span>
          </p>

          <p v-if="isEvaluation && expirationDate" class="dea-product-card__activation-date">
            In Valutazione fino al {{ expirationDate }}
          </p>
          <p v-else-if="activationDate" class="dea-product-card__activation-date">
            Libro attivato il {{ activationDate }}
          </p>
          <Links
            v-if="showModalLink"
            class="dea-product-card__book-link dea-links--small"
            @click.prevent="handleClick($event, 'info-click')"
          >
            Ulteriori informazioni
          </Links>
          <br />
          <Links
            v-if="showCatalogLink"
            :class="['dea-product-card__book-link', 'dea-links--small', `dea-links--${linkColor}`]"
            :href="catalogLink"
          >
            Scheda libro nel Catalogo
          </Links>
        </template>
      </template>
      <template v-else-if="showCatalogLink">
        <Buttons
          class="dea-button dea-button--medium dea-button--iconLeft"
          type="a"
          :href="minisiteLink"
          icon="minisito"
          >Vai al minisito del libro</Buttons
        >
        <Links class="dea-links--large dea-links--target-color-blue-docenti" :href="link" icon="go-to"
          >Vai al catalogo</Links
        >
      </template>
    </div>
  </div>
</template>

<script>
import importantIcoNeg from "@/assets/icons/evidenza_neg.svg";
import importantIcoPos from "@/assets/icons/evidenza_pos.svg";
import utils from "@/components/mixins/utils";
import Badge from "../elements/Badge";
import Buttons from "../elements/Buttons";
import Links from "../elements/Links";
import Tooltip from "../elements/Tooltip";

export default {
  name: "CardProduct",

  components: { Badge, Buttons, Links, Tooltip },
  props: {
    private: {
      type: Boolean,
      default: false,
    },
    activable: {
      type: Boolean,
      default: false,
    },
    activationDate: {
      type: String,
      default: "",
    },
    author: {
      type: String,
      default: "",
    },
    badgeType: {
      type: String,
      default: "",
    },
    bookmark: {
      type: Object,
      default: () => {},
    },
    cardType: {
      type: String,
      default: "normal",
    },
    catalogLink: {
      type: String,
      default: "",
    },
    collection: {
      type: String,
      default: "",
    },
    showExtraInfo: {
      type: Boolean,
      default: false,
    },
    device: {
      type: String,
      default: "",
    },
    editor: {
      type: String,
      default: "",
    },
    expirationDate: {
      type: String,
      default: "",
    },
    expired: {
      type: Boolean,
      default: false,
    },
    image: {
      type: String,
      default: "",
    },
    level: {
      type: String,
      default: "",
    },
    link: {
      type: String,
      default: null,
    },
    minisiteLink: {
      type: String,
      default: "",
    },
    schoolTarget: {
      type: String,
      default: "",
    },
    showCatalogLink: {
      type: Boolean,
      default: false,
    },
    subtitle: {
      type: String,
      default: "",
    },
    suspended: {
      type: Boolean,
      default: false,
    },
    target: {
      type: String,
      default: null,
    },
    timeId: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "app",
    },
    showBookmark: {
      type: Boolean,
      default: false,
    },
    showModalLink: {
      type: Boolean,
      default: true,
    },
    volumes: {
      type: Number,
      default: null,
    },
    linkColor: {
      type: String,
      default: "",
    },
    activationDetails: {
      type: Boolean,
      default: false,
    },
    isEvaluation: {
      type: Boolean,
      default: false,
    },
    demo: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      importantIcoNeg,
      importantIcoPos,
      showTooltip: false,
    };
  },
  computed: {
    badge() {
      if (this.badgeType) return this.badgeType;
      if (this.suspended) return "suspended";
      if (this.demo) return "demo";
      return "";
    },
    fuoriCatalogo() {
      return this.private;
    },
    linkType() {
      if (this.link) {
        return "a";
      }
      return "div";
    },
  },
  watch: {
    bookmark() {
      if (utils.isTouchDevice()) {
        if (this.bookmark) {
          this.showTooltip = true;
          setTimeout(() => {
            this.showTooltip = false;
          }, 2000);
        }
      }
    },
  },
  methods: {
    isMobile(value) {
      return value === "mobile";
    },
    isDesktop(value) {
      return value === "desktop";
    },
    isApp(value) {
      return value === "app";
    },
    isBook(value) {
      return value === "book";
    },
    /**
     * Centralized event handling.
     *
     * Depending on type and links passed handle the different cases.
     *
     * Possible custom event types:
     *
     * - "title-click": click on the work title
     * - "cover-click": click on the work cover
     * - "info-click": click on the more info link -> emits "open-info" custom event
     * - "detail-click": click on the go to catalog link -> load this.catalogLink in current window
     *
     * title-click / cover-click has a mixed behavior:
     *
     * - if minisiteLink is set -> open the minisite (default behavior for teacher myhome )
     * - if link is set -> open the link (default behavior for generic books listing )
     * - if neither is set -> emit "open-minisite" (used to handle child confirmation modal)
     *
     *
     * @param event: dom event
     * @param type: custom event type
     */
    handleClick(event, type) {
      if (["title-click", "cover-click"].indexOf(type) > -1) {
        if (this.minisiteLink) {
          window.open(this.minisiteLink);
        } else if (this.link) {
          document.location.href = this.link;
        } else {
          this.$emit("open-minisite");
        }
        this.$emit(type);
      } else if (type === "info-click") {
        this.$emit("open-info");
      } else if (type === "detail-click") {
        document.location.href = this.catalogLink;
      }
    },
    toggleTooltip() {
      if (!utils.isTouchDevice()) {
        this.showTooltip = !this.showTooltip;
      }
    },
    toggleBookmark() {
      this.$emit("toggle-bookmark");
    },
  },
};
</script>
<style lang="scss">
// TODO: fix image when app modifier is active
@import "@/styles/system.utils";
</style>
